import { Empty, Typography } from 'antd';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { Sheet } from 'react-modal-sheet';
import { ReactComponent as BackButton } from '../../../Assets/left-arrow.svg';
import { ReactComponent as PbMoney } from '../../../Assets/pbmoney.svg';
import { ReactComponent as Warning } from '../../../Assets/warning-circle.svg';

import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../../App';
import { ReactComponent as NoTxn } from '../../../Assets/noTxn.svg';
import { ReactComponent as Right } from '../../../Assets/right-arrow.svg';
import { EVENTCODES, pushEventToParent } from '../../../Utils/eventUtils';
import { formatToINR } from '../../../Utils/utils';
import Transaction from '../../Dashboard/Common/Transaction';
import styles from './index.module.css';
import HeaderSubHeader from '../Header/HeaderSubHeader';
import NavigationHeader from '../Header/NavigationHeader';

const RecurringTransactions: React.FC<{
  selectedAccounts: any;
  isFilterOptions?: boolean;
  isViewTransationVisible?: boolean;
  showAllPayments?: boolean;
  onGoback?: any;
}> = ({
  selectedAccounts,
  isFilterOptions = false,
  isViewTransationVisible = false,
  showAllPayments = false,
  onGoback,
}) => {
  const [allTransactions, setAllTransactions] = useState<any>([]);
  const { data, setBackButtonHandler }: any = useContext(AppContext);
  const [isViewAllTransactions, setIsViewAllTransactions] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPendingAmount, setTotalPendingAmount] = useState(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);

  const [completedTxn, setCompletedTxn] = useState<any[]>([]);
  const [pendingTxn, setPendingTxn] = useState<any[]>([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState<any[]>([]);

  const navigate = useNavigate();

  // Helper function to format category names
  const formatCategoryName = (categoryCode: string): string => {
    if (!categoryCode) return 'Others';

    const categoryMap: { [key: string]: string } = {
      SUBSCRIPTION: 'Subscriptions',
      UTILITIES: 'Bills & Utilities',
      EMI: 'EMI Payments',
      INSURANCE: 'Insurance',
      MUTUAL_FUND: 'SIP/Investment',
      RENT: 'Rent',
      LOAN: 'Loan Payments',
      OTHERS: 'Others',
    };

    if (categoryMap[categoryCode]) {
      return categoryMap[categoryCode];
    }

    return categoryCode
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Process category breakdown from transactions
  const processCategoryBreakdown = (transactions: any[]) => {
    const categories: {
      [key: string]: {
        count: number;
        totalAmount: number;
        transactions: any[];
      };
    } = {};

    transactions.forEach((txn: any) => {
      const category = txn.categoryCode || txn.category || 'OTHERS';
      if (!categories[category]) {
        categories[category] = { count: 0, totalAmount: 0, transactions: [] };
      }
      categories[category].count += 1;
      categories[category].totalAmount += txn.lastAmount || 0;
      categories[category].transactions.push(txn);
    });

    return Object.entries(categories).map(([category, data]) => ({
      category: formatCategoryName(category),
      originalCategory: category,
      count: data.count,
      totalAmount: data.totalAmount,
      transactions: data.transactions,
    }));
  };

  useEffect(() => {
    if (isViewTransationVisible) {
      window.onpopstate = () => {
        // Push the user forward again to prevent back
        window.history.pushState(null, '', window.location.href);
      };
      setBackButtonHandler(() => {
        return () => {
          pushEventToParent({
            eventCode: EVENTCODES.BACK_BUTTON_RECURRING,
          });
          setIsViewAllTransactions(false);
          onGoback && onGoback();
        };
      });
    }
  }, [isViewTransationVisible]);

  const getTransactionApiCall = () => {
    let amount = 0;
    let amountPaid = 0;
    let amountPending = 0;

    let filteredData = [];

    if (
      data &&
      data.current &&
      data.current.ASSET &&
      data.current.ASSET.recurringAnalysis &&
      data.current.ASSET.recurringAnalysis.MONTHLY
    ) {
      data.current.ASSET.recurringAnalysis.MONTHLY.sort((a: any, b: any) => {
        const dateA = dayjs(a.lastTransactionDate);
        const dateB = dayjs(b.lastTransactionDate);

        return dateA.isAfter(dateB) ? 1 : -1;
      });
      if (selectedAccounts) {
        filteredData = data.current.ASSET.recurringAnalysis.MONTHLY.filter(
          (accounts: any) =>
            accounts.account.slice(-6) === selectedAccounts.accNo &&
            accounts.active
        );
      } else {
        filteredData = data.current.ASSET.recurringAnalysis.MONTHLY.filter(
          (accounts: any) => accounts.active
        );
      }
    }

    const todayDate = dayjs(new Date());
    const currentDate = parseInt(todayDate.format('D'));
    const currentMonth = parseInt(todayDate.format('M'));

    filteredData.forEach((txn: any) => {
      const txnDate = dayjs(txn.lastTransactionDate);
      const txnDateDay = parseInt(txnDate.format('D'));
      const txnDatetMonth = parseInt(txnDate.format('M'));

      const isTxnCompleted =
        currentDate - txnDateDay > 0 && currentMonth === txnDatetMonth
          ? true
          : false;

      if (isTxnCompleted) {
        let completedTxnLocal = completedTxn;
        completedTxnLocal.push(txn);
        setCompletedTxn(completedTxnLocal);
        amountPaid += txn.lastAmount;
      } else {
        let pendingTxnLocal = pendingTxn;
        pendingTxnLocal.push(txn);
        setPendingTxn(pendingTxnLocal);
        amountPending += txn.lastAmount;
      }

      amount += txn.lastAmount;
    });

    setTotalAmount(amount);
    setTotalPendingAmount(amountPending);
    setTotalPaidAmount(amountPaid);

    setAllTransactions(
      isFilterOptions ? filteredData : filteredData.slice(0, 4)
    );

    // Process and set category breakdown
    const breakdown = processCategoryBreakdown(filteredData);
    setCategoryBreakdown(breakdown);
  };

  useEffect(() => {
    getTransactionApiCall();
  }, []);

  const showItems = () => {
    return allTransactions.length > 0 ? (
      <>
        {allTransactions.map((txn: any, i: number) => {
          txn.id = i;
          txn.format = true;

          return (
            <Transaction
              showTransactionDetails={true}
              transaction={txn}
              showDateOfTransaction={true}
            />
          );
        })}
      </>
    ) : (
      <></>
    );
  };

  const showItemsFullScreen = () => {
    return allTransactions.length > 0 ? (
      <>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
          }}
        >
          <div
            className={
              completedTxn.length === 0 || pendingTxn.length === 0
                ? ''
                : styles.borderBottom
            }
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '20px 16px',
              border: '1px solid rgb(255, 229, 191)',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography.Text
                style={{
                  color: '#6A6D71',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Pending
              </Typography.Text>
              <Typography.Text className={styles.topAmountSection}>
                {formatToINR(totalPendingAmount.toString())}
              </Typography.Text>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
              }}
            >
              <Typography.Text
                style={{
                  color: '#6A6D71',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Paid
              </Typography.Text>
              <Typography.Text className={styles.topAmountSection}>
                {formatToINR(totalPaidAmount.toString())}
              </Typography.Text>
            </div>
          </div>

          {completedTxn.length === 0 || pendingTxn.length === 0 ? (
            <div
              style={{
                padding: 12,
                background: '#FFFAF2',
                border: '1px solid #FFE5BF',
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                display: 'flex',
                gap: 8,
              }}
            >
              <Warning />
              <Typography.Text
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#CC7A00',
                  width: '100%',
                }}
              >
                {completedTxn.length === 0
                  ? `We could not detect any recurring transactions paid this month.`
                  : `No pending payments remain for this month.`}
              </Typography.Text>
            </div>
          ) : (
            <></>
          )}
        </div>

        {/* Category Breakdown Section */}
        {categoryBreakdown.length > 0 && (
          <div style={{ margin: '20px 0' }}>
            <Typography.Text
              style={{
                color: '#45484D',
                fontSize: 16,
                fontWeight: 600,
                marginBottom: 12,
                display: 'block',
              }}
            >
              CATEGORY BREAKDOWN
            </Typography.Text>
            {categoryBreakdown.map((cat: any, index: number) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  margin: '8px 0',
                  background: '#F8F9FA',
                  borderRadius: 8,
                  border: '1px solid #E7E7E8',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography.Text
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#1C1D1F',
                    }}
                  >
                    {cat.category}
                  </Typography.Text>
                  <Typography.Text
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: '#6A6D71',
                    }}
                  >
                    {cat.count} payment{cat.count !== 1 ? 's' : ''}
                  </Typography.Text>
                </div>
                <Typography.Text
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#1C1D1F',
                  }}
                >
                  {formatToINR(cat.totalAmount.toString())}
                </Typography.Text>
              </div>
            ))}
          </div>
        )}

        {completedTxn.length > 0 ? (
          <>
            <Typography.Text
              style={{
                color: '#45484D',
                fontSize: 10,
                fontWeight: 600,
                marginTop: 24,
              }}
            >
              PAID THIS MONTH
            </Typography.Text>

            {completedTxn.map((txn: any, i: number) => {
              txn.id = i;
              txn.format = true;

              return (
                <Transaction
                  className={
                    completedTxn.length - 1 === i && pendingTxn.length === 0
                      ? styles.transactionCard
                      : ''
                  }
                  showTransactionDetails={true}
                  transaction={txn}
                  showDateOfTransaction={true}
                  isCompletedRec={true}
                />
              );
            })}
          </>
        ) : (
          <></>
        )}

        {pendingTxn.length > 0 ? (
          <>
            <Typography.Text
              style={{
                color: '#45484D',
                fontSize: 10,
                fontWeight: 600,
                marginTop: 24,
              }}
            >
              PENDING PAYMENTS
            </Typography.Text>

            {pendingTxn.map((txn: any, i: number) => {
              txn.id = i;
              txn.format = true;

              return (
                <Transaction
                  className={
                    pendingTxn.length - 1 !== i ? '' : styles.transactionCard
                  }
                  showTransactionDetails={true}
                  transaction={txn}
                  showDateOfTransaction={true}
                  isPendingRec={true}
                />
              );
            })}
          </>
        ) : (
          <></>
        )}
      </>
    ) : (
      <Empty
        style={{ marginTop: 100 }}
        image={<NoTxn />}
        description={
          <Typography.Text
            style={{ fontSize: 16, fontWeight: 500, opacity: 0.5 }}
          >
            No transaction found
          </Typography.Text>
        }
      />
    );
  };

  return !isFilterOptions &&
    allTransactions.length === 0 &&
    !showAllPayments ? (
    <></>
  ) : (
    <>
      <div
        className={`${styles.transactionContainer} transactionContainer ${
          isFilterOptions ? styles.padding24 : ''
        }`}
      >
        {isViewAllTransactions || showAllPayments ? (
          <Sheet
            isOpen={isViewAllTransactions || showAllPayments}
            onClose={() => {}}
            detent='full-height'
            draggable={false}
            className='recurringTransactionsSheet'
          >
            <Sheet.Container>
              <div className={`${styles.allTxn}`}>
                <NavigationHeader
                  header={'My Recurring Payments'}
                  onBack={() => {
                    if (isViewTransationVisible) {
                      pushEventToParent({
                        eventCode: EVENTCODES.BACK_BUTTON_RECURRING,
                      });
                      setIsViewAllTransactions(false);
                      onGoback && onGoback();
                    }
                  }}
                />

                <RecurringTransactions
                  selectedAccounts={selectedAccounts}
                  isFilterOptions={true}
                />
              </div>
            </Sheet.Container>
          </Sheet>
        ) : (
          <>
            <HeaderSubHeader header='We detected the following recurring payments for your account' />

            <div
              className={
                isFilterOptions
                  ? styles.transactionSection
                  : styles.transactionSection2
              }
            >
              {!isFilterOptions ? showItems() : showItemsFullScreen()}

              {isViewTransationVisible && allTransactions.length > 0 ? (
                <Typography.Link
                  onClick={() => {
                    navigate('/recurringPayments');
                    pushEventToParent({
                      eventCode: EVENTCODES.VIEW_ALL_RECURRING,
                    });
                    setIsViewAllTransactions(true);
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    color: '#0066FF',
                    fontSize: 12,
                    width: 'auto',
                    fontWeight: 600,
                    marginTop: 16,
                  }}
                >
                  View all <Right style={{ marginLeft: 4 }} />
                </Typography.Link>
              ) : (
                <></>
              )}
            </div>
          </>
        )}
      </div>

      {isFilterOptions && totalAmount ? (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 16px',
              background: '#F2F7FF',
            }}
          >
            <Typography.Text
              style={{
                color: 'var(--pb-neutral)',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              TOTAL
            </Typography.Text>
            <Typography.Text style={{ fontSize: 16, fontWeight: 600 }}>
              {formatToINR(totalAmount.toString())}
            </Typography.Text>
          </div>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default RecurringTransactions;
